// This optional code is used to register a service worker.
// register() is not called by default.

// This lets the app load faster on subsequent visits in production, and gives
// it offline capabilities. However, it also means that developers (and users)
// will only see deployed updates on subsequent visits to a page, after all the
// existing tabs open on the page have been closed, since previously cached
// resources are updated in the background.

// To learn more about the benefits of this model and instructions on how to
// opt-in, read https://bit.ly/CRA-PWA

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
    // [::1] is the IPv6 localhost address.
    window.location.hostname === '[::1]' ||
    // 127.0.0.0/8 are considered localhost for IPv4.
    window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/)
);

const UPDATE_INTERVAL_MS = 2 * 60 * 1000;
const SCRIPT_REQUEST_HEADERS = {
  'Cache-Control': 'no-cache',
  'Service-Worker': 'script',
};

let hasPendingServiceWorkerUpdate = false;
let hasControllerChanged = false;

export function register(config) {
  if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
    // The URL constructor is available in all browsers that support SW.
    const publicUrl = new URL(process.env.PUBLIC_URL, window.location.href);
    if (publicUrl.origin !== window.location.origin) {
      // Our service worker won't work if PUBLIC_URL is on a different origin
      // from what our page is served on. This might happen if a CDN is used to
      // serve assets; see https://github.com/facebook/create-react-app/issues/2374
      return;
    }

    window.addEventListener('load', () => {
      const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;
      checkValidServiceWorker(swUrl, config);

      if (isLocalhost) {
        // Add some additional logging to localhost, pointing developers to the
        // service worker/PWA documentation.
        navigator.serviceWorker.ready.then(() => {
          console.log(
            'This web app is being served cache-first by a service ' +
              'worker. To learn more, visit https://bit.ly/CRA-PWA'
          );
        });
      }
    });
  }
}

function registerValidSW(swUrl, config) {
  navigator.serviceWorker
    .register(swUrl)
    .then((registration) => {
      registration.onupdatefound = () => {
        const installingWorker = registration.installing;
        if (installingWorker == null) {
          return;
        }
        installingWorker.onstatechange = () => {
          if (installingWorker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              // At this point, the updated precached content has been fetched,
              // but the previous service worker will still serve the older
              // content until all client tabs are closed.
              console.log(
                'New content is available and will be used when all ' +
                  'tabs for this page are closed. See https://bit.ly/CRA-PWA.'
              );

              // Execute callback
              if (config && config.onUpdate) {
                config.onUpdate(registration);
              }
            } else {
              // At this point, everything has been precached.
              // It's the perfect time to display a
              // "Content is cached for offline use." message.
              console.log('Content is cached for offline use.');

              // Execute callback
              if (config && config.onSuccess) {
                config.onSuccess(registration);
              }
            }
          }
        };
      };

      setInterval(() => {
        registration.update().catch(async (error) => {
          if (isServiceWorkerScriptError(error)) {
            await recoverInvalidServiceWorker(swUrl);
            return;
          }
          console.warn('Service worker update failed:', error);
        });
      }, UPDATE_INTERVAL_MS);
    })
    .catch(async (error) => {
      if (isServiceWorkerScriptError(error)) {
        await recoverInvalidServiceWorker(swUrl);
        return;
      }
      console.error('Error during service worker registration:', error);
    });

  const serviceWorkerAlreadyInstalled = !!navigator.serviceWorker.controller;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!serviceWorkerAlreadyInstalled || hasControllerChanged) return;
    hasControllerChanged = true;
    console.log('New active service worker loaded. Will switch to new service worker on route change.');
    hasPendingServiceWorkerUpdate = true;
  });
}

function checkValidServiceWorker(swUrl, config) {
  // Check if the service worker can be found. If it can't reload the page.
  fetch(swUrl, {
    cache: 'no-store',
    headers: SCRIPT_REQUEST_HEADERS,
  })
    .then(async (response) => {
      // Ensure service worker exists, and that we really are getting a JS file.
      if (await isInvalidServiceWorkerResponse(response)) {
        // No service worker found. Probably a different app. Reload the page.
        await recoverInvalidServiceWorker(swUrl);
      } else {
        // Service worker found. Proceed as normal.
        registerValidSW(swUrl, config);
      }
    })
    .catch(() => {
      console.log('No internet connection found. App is running in offline mode.');
    });
}

export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister();
      })
      .catch((error) => {
        console.error(error.message);
      });
  }
}

async function isInvalidServiceWorkerResponse(response) {
  if (!response.ok) return true;

  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('javascript')) return false;

  const bodyStart = (await response.clone().text()).trim().slice(0, 100);

  if (contentType.includes('text/html')) return true;
  if (bodyStart.startsWith('<!DOCTYPE html') || bodyStart.startsWith('<html') || bodyStart.startsWith('<')) {
    return true;
  }

  // Some hosts omit the content type for generated assets; tolerate non-HTML responses.
  return false;
}

function isServiceWorkerScriptError(error) {
  const message = error && error.message ? error.message : '';
  return /service-?worker|service worker|script|load failed|unexpected token/i.test(message);
}

async function recoverInvalidServiceWorker(swUrl) {
  try {
    const registration = await navigator.serviceWorker.getRegistration(swUrl);
    if (registration) {
      await registration.unregister();
    }
  } catch (error) {
    console.warn('Failed to unregister invalid service worker:', error);
  }

  if (navigator.serviceWorker.controller) {
    window.location.reload();
  }
}

export function shouldRefreshForServiceWorkerUpdate() {
  return hasPendingServiceWorkerUpdate;
}
